import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('medical-pi-cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('medical-pi-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existingItem = prev.find(item => item.id === product.id);
      if (existingItem) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('medical-pi-cart');
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Add product by name (for AI chatbot)
  const addToCartByName = (productName, quantity = 1) => {
    // Product mapping for common names
    const productMapping = {
      'handschuhe': { id: 'gloves_m', name: 'Einmalhandschuhe M (100 Stk.)', price: 8.50, category: 'Handschuhe' },
      'einmalhandschuhe': { id: 'gloves_m', name: 'Einmalhandschuhe M (100 Stk.)', price: 8.50, category: 'Handschuhe' },
      'desinfektion': { id: 'sanitizer', name: 'Händedesinfektion 500ml', price: 6.90, category: 'Desinfektion' },
      'händedesinfektion': { id: 'sanitizer', name: 'Händedesinfektion 500ml', price: 6.90, category: 'Desinfektion' },
      'desinfektionsmittel': { id: 'sanitizer', name: 'Händedesinfektion 500ml', price: 6.90, category: 'Desinfektion' },
      'mundschutz': { id: 'masks', name: 'Mundschutz (50 Stk.)', price: 5.50, category: 'Schutzausrüstung' },
      'masken': { id: 'masks', name: 'Mundschutz (50 Stk.)', price: 5.50, category: 'Schutzausrüstung' },
      'bettschutz': { id: 'bed_pads', name: 'Bettschutzeinlagen (25 Stk.)', price: 12.90, category: 'Inkontinenz' },
      'bettschutzeinlagen': { id: 'bed_pads', name: 'Bettschutzeinlagen (25 Stk.)', price: 12.90, category: 'Inkontinenz' },
      'unterlagen': { id: 'bed_pads', name: 'Bettschutzeinlagen (25 Stk.)', price: 12.90, category: 'Inkontinenz' },
      'fingerlinge': { id: 'finger_cots', name: 'Fingerlinge (100 Stk.)', price: 4.90, category: 'Handschuhe' },
      'schutzschürzen': { id: 'aprons', name: 'Schutzschürzen (50 Stk.)', price: 9.90, category: 'Schutzausrüstung' },
      'schürzen': { id: 'aprons', name: 'Schutzschürzen (50 Stk.)', price: 9.90, category: 'Schutzausrüstung' },
      'flächendesinfektion': { id: 'surface_disinfectant', name: 'Flächendesinfektion 1L', price: 8.50, category: 'Desinfektion' },
    };

    const searchName = productName.toLowerCase().trim();
    const product = productMapping[searchName];
    
    if (product) {
      addToCart(product, quantity);
      return { success: true, product };
    }
    
    // Partial match
    for (const [key, prod] of Object.entries(productMapping)) {
      if (searchName.includes(key) || key.includes(searchName)) {
        addToCart(prod, quantity);
        return { success: true, product: prod };
      }
    }
    
    return { success: false, message: `Produkt "${productName}" nicht gefunden` };
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    addToCartByName
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
